/**
 * Hymnal reference pages — the shared model + matrix enumeration for the public
 * hymnal SEO engine: one crawlable page per hymn, plus threshold-gated tune,
 * person and meter pages. See `docs/pm/active/fugue-lab-hymnal-seo.md`.
 *
 * Pure: no React, no AWS, no I/O — the caller supplies the parsed source. The page
 * TEMPLATES stay in the consuming site (design); the MODEL is shared so the router,
 * the prerenderer's `extraRoutes` and the sitemap cannot drift. Mirrors the
 * `local-pages.ts` precedent in `@gjl/site-kit`.
 *
 * TWO RULES THIS FILE EXISTS TO ENFORCE:
 *
 * 1. ONE SOURCE. Callers pass the SAME `data/hymnal/hymnal_1982.json` the Canticle
 *    seed reads (`scripts/seed-lectionary-hymnal.ts`). Never a fork — a fork is how
 *    the public site and Canticle come to disagree about hymn 272.
 *
 * 2. SLUGS DERIVE FROM THE SOURCE, NEVER FROM CANTICLE IDS. Canticle's `Tune` and
 *    `Contributor` records are ORG-SCOPED (`pk=ORG#{orgId}`, `sk=TUNE#{id}`), so the
 *    same tune carries a different id in every tenant and no Canticle id is a stable
 *    public identifier. Every slug here is a pure function of the source text, which
 *    makes it identical for every consumer and stable across reseeds.
 *
 * THRESHOLD GATING IS DELIBERATE. 720 hymns all warrant a page; the secondary axes
 * do not (570 distinct tunes, but 446 appear on exactly one hymn). Entities below
 * threshold still render as SECTIONS on the hymn page — no data is hidden, it just
 * does not get its own thin URL. `local-pages.ts` is gated the same way for the same
 * anti-doorway reason.
 */
import { sectionOf, sectionRoute } from './hymnal-sections.js';
export const PERSON_ROLES = [
    'author',
    'translator',
    'composer',
    'arranger',
    'harmonizer',
];
export const DEFAULT_THRESHOLDS = {
    tuneMinHymns: 2,
    /**
     * 1 — every credited person gets a page. Garrett's call, 2026-08-09; this
     * PARTLY REVERSES D4 of `docs/pm/active/fugue-lab-hymnal-seo.md`, which gated
     * every secondary axis at 3 for anti-doorway reasons.
     *
     * Why the reversal is safe here and not in general: the gate was closing a
     * thin-content risk, but it was ALSO the cause of 619 credit links that
     * resolved to nothing (the hymn template linked every credit unconditionally
     * while only 108 of 607 people had a page). Gating traded a real, present
     * defect for a hypothetical ranking one.
     *
     * Measured at this threshold on the real corpus: 607 people, 0 slug
     * collisions, 0 suspicious merges. The thin-content mitigation is on the
     * PersonPage template — variants, roles, section, and cross-links to everyone
     * else credited on the same hymn — so a one-hymn page is a node in a graph
     * rather than a leaf.
     *
     * Tunes stay at 2: their links are already guarded, and 445 single-hymn tunes
     * would be genuinely contentless.
     */
    personMinHymns: 1,
    meterMinHymns: 10,
};
/**
 * Trailing life dates and editorial qualifiers, as credited in the source:
 *   "Ralph Vaughan Williams, 1872-1958"   -> "Ralph Vaughan Williams"
 *   "David Hurd, b. 1950"                 -> "David Hurd"
 *   "John Mason Neale, 1818-1866 (alt.)"  -> "John Mason Neale"
 *   "The Venerable Bede, 673-735"         -> "The Venerable Bede"
 *
 * Stripping is for the SLUG only — `PersonPage.name` keeps the full credit, because
 * the dates are what disambiguate two musicians who share a name. Any collision this
 * introduces is a hard error, not a silent merge: see `findSlugCollisions`.
 *
 * ⚠️ THE PATTERN HAS TO COVER EVERY WAY THE SOURCE WRITES A DATE, because a form it
 * misses does not degrade — it SPLITS A PERSON ACROSS TWO PAGES. Measured on the
 * real corpus 2026-08-09: the original pattern handled only `, 1872-1958` and
 * `, b. 1950`, and the five forms it missed split ten people, Cyril Vincent Taylor
 * across three pages ("…, 1907-" / "…, b. 1907-" / no dates at all). That is exactly
 * the failure the merge logic above exists to prevent, and it stayed invisible while
 * people were gated at >=3 hymns because most of the fragments were sub-threshold.
 *
 * The forms, all real:
 *   uncertain   "Thomas Tallis, 1505?-1585" · "Clement of Alexandria, 170?-220?"
 *   open-ended  "Alec Wyton, 1921-" · "Cyril Vincent Taylor, b. 1907-"
 *   parenthesized "Eleanor Farjeon (1881-1965)" · "Anon. (1625)"
 *   doubled dash  "Craig Sellar Lang, 1891--1971"
 *   dot-separated "A. Gregory Murray. b. 1905"
 *
 * Anchored at the end and requiring 3-4 digits, so it cannot eat a name.
 */
const LIFE_DATES = /[,.]?\s*\(?\s*(?:b\.|d\.|c\.|ca\.|fl\.)?\s*\d{3,4}\??\s*(?:-{1,2}\s*(?:\d{3,4}\??)?)?\s*\)?\s*$/;
const QUALIFIER = /\s*\((?:alt\.?|adapt\.?|attr\.?|para\.?|rev\.?)[^)]*\)\s*$/gi;
/**
 * A separator the source leaves dangling once a qualifier is removed.
 *
 * `"James Montgomery, 1771-1854, (alt.)"` — note the comma BEFORE the qualifier —
 * becomes `"James Montgomery, 1771-1854,"` after the qualifier goes, and
 * `LIFE_DATES` is `$`-anchored so it no longer matches. That one stray comma split
 * James Montgomery across two pages while his other seven credits merged
 * correctly. Stripping it inside the loop lets the next pass see the dates.
 */
const TRAILING_SEPARATOR = /[,;]\s*$/;
/** Strip editorial qualifiers, dangling separators and trailing life dates, to a
 *  fixpoint. Order matters — the qualifier sits AFTER the dates in the source, and
 *  removing one can expose another. */
export function personDisplayToSlugBase(name) {
    let n = String(name ?? '').trim();
    let prev = '';
    while (n !== prev) {
        prev = n;
        n = n.replace(QUALIFIER, '').trim();
        n = n.replace(TRAILING_SEPARATOR, '').trim();
        n = n.replace(LIFE_DATES, '').trim();
    }
    return n;
}
/**
 * URL slug: lowercase, accents folded, non-alphanumerics collapsed to single dashes.
 *
 * Applied to names ("Ralph Vaughan Williams" -> `ralph-vaughan-williams`), tune names
 * ("THE TRUTH FROM ABOVE" -> `the-truth-from-above`) and meters ("87. 87. 87" ->
 * `87-87-87`, "Irr." -> `irr`).
 */
export function slugify(value) {
    return String(value ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
const clean = (v) => {
    const s = String(v ?? '').trim();
    return s === '' ? undefined : s;
};
/** `"272. The great forerunner of the morn"` -> `"The great forerunner of the morn"`. */
const stripNumberPrefix = (title) => String(title ?? '')
    .replace(/^\s*\d+[a-z]?\s*\.\s*/i, '')
    .trim();
/** Hymn numbers can carry a letter suffix (`"143"`, `"143a"`); order numerically,
 *  then by suffix, so `143` precedes `143a` precedes `144`. */
export function hymnOrder(number) {
    const m = /^(\d+)\s*([a-z]?)/i.exec(String(number ?? ''));
    if (!m)
        return Number.MAX_SAFE_INTEGER;
    const n = Number(m[1]);
    const suffix = m[2] ? m[2].toLowerCase().charCodeAt(0) - 96 : 0;
    return n * 100 + suffix;
}
const HAS_QUALIFIER = /\((?:alt\.?|adapt\.?|attr\.?|para\.?|rev\.?)[^)]*\)/i;
/** Full life-date range, e.g. `1872-1958`. Used to grade credit completeness. */
const FULL_DATES = /,\s*(?:b\.|d\.|c\.|fl\.)?\s*\d{4}\s*-\s*\d{4}\s*(?:\(|$)/;
/**
 * Pick the cleanest credit among the spellings a person appears under.
 *
 * Preference order, each a tiebreak on the last: no editorial qualifier → carries a
 * complete four-digit date range → most frequently used in the source → longest →
 * lexical (so the result is deterministic, never insertion-ordered).
 */
export function canonicalCredit(seen) {
    const grade = (s) => [
        HAS_QUALIFIER.test(s) ? 1 : 0, // 0 sorts first — prefer no qualifier
        FULL_DATES.test(s) ? 0 : 1, // prefer a complete date range
        -(seen.get(s) ?? 0), // prefer the more common spelling
        -s.length, // prefer the more complete string
    ];
    return [...seen.keys()].sort((a, b) => {
        const ga = grade(a);
        const gb = grade(b);
        for (let i = 0; i < ga.length; i++)
            if (ga[i] !== gb[i])
                return (ga[i] ?? 0) - (gb[i] ?? 0);
        return a.localeCompare(b);
    })[0];
}
/** Route for a hymn page. All route builders live here so the router, the
 *  prerenderer and the sitemap read one definition. */
export const hymnRoute = (source, slug) => `/hymnal/${source.id}/hymn/${slug}`;
export const tuneRoute = (source, slug) => `/hymnal/${source.id}/tune/${slug}`;
export const personRoute = (source, slug) => `/hymnal/${source.id}/person/${slug}`;
export const meterRoute = (source, slug) => `/hymnal/${source.id}/meter/${slug}`;
export const hymnalHomeRoute = (source) => `/hymnal/${source.id}`;
/**
 * Build every page model for one hymnal.
 *
 * Deterministic: the same input always yields the same slugs, in the same order.
 * Entries with `exists === false` are skipped (a gap in the source, not a hymn).
 */
export function enumerateHymnalPages(source, raw, thresholds = DEFAULT_THRESHOLDS) {
    const entries = Object.values(raw ?? {}).filter((h) => h && h.exists !== false);
    // --- hymn pages -----------------------------------------------------------
    const hymns = entries
        .map((h) => {
        const text = h.text ?? {};
        const tune = h.tune ?? {};
        const firstLine = clean(text.first_line) ?? stripNumberPrefix(h.hymn_title ?? '');
        const tuneName = clean(tune.name);
        const meter = clean(tune.meter) ?? clean(text.meter);
        const section = source.sections ? sectionOf(source.sections, h.number) : undefined;
        return {
            slug: slugify(h.number),
            number: String(h.number),
            order: hymnOrder(h.number),
            title: firstLine,
            firstLine,
            author: clean(text.author),
            translator: clean(text.translator),
            textMeter: clean(text.meter),
            language: clean(text.language),
            publicationDate: clean(text.publication_date),
            tuneName,
            tuneSlug: tuneName ? slugify(tuneName) : undefined,
            composer: clean(tune.composer),
            arranger: clean(tune.arranger),
            harmonizer: clean(tune.harmonizer),
            meter,
            meterSlug: meter ? slugify(meter) : undefined,
            key: clean(tune.key),
            section: section?.label,
            sectionSlug: section?.slug,
            season: section?.season,
        };
    })
        .sort((a, b) => a.order - b.order);
    const refOf = (h) => ({ number: h.number, title: h.title, slug: h.slug });
    // --- tune pages -----------------------------------------------------------
    const tuneAcc = new Map();
    for (const h of hymns) {
        if (!h.tuneName || !h.tuneSlug)
            continue;
        const existing = tuneAcc.get(h.tuneSlug);
        if (existing) {
            existing.hymns.push(refOf(h));
            continue;
        }
        tuneAcc.set(h.tuneSlug, {
            slug: h.tuneSlug,
            name: h.tuneName,
            meter: h.meter,
            key: h.key,
            composer: h.composer,
            arranger: h.arranger,
            harmonizer: h.harmonizer,
            hymns: [refOf(h)],
        });
    }
    // --- person pages ---------------------------------------------------------
    // Identity is the SLUG BASE, not the raw credit. Measured on the real corpus
    // (2026-08-01): keying by raw credit produced 71 slug collisions, every one of
    // them the SAME person credited inconsistently — qualifiers, stray whitespace,
    // and year typos. Keying by raw credit would split Ralph Vaughan Williams across
    // three pages and John Mason Neale across four. Every spelling is preserved in
    // `variants` so the merge is auditable rather than silent.
    const personAcc = new Map();
    const addPerson = (name, role, h) => {
        const display = clean(name);
        if (!display)
            return;
        const slug = slugify(personDisplayToSlugBase(display));
        if (!slug)
            return;
        const existing = personAcc.get(slug);
        if (existing) {
            existing.seen.set(display, (existing.seen.get(display) ?? 0) + 1);
            if (!existing.roles.includes(role))
                existing.roles.push(role);
            if (!existing.hymns.some((x) => x.number === h.number))
                existing.hymns.push(refOf(h));
            return;
        }
        personAcc.set(slug, {
            slug,
            name: display,
            variants: [],
            roles: [role],
            hymns: [refOf(h)],
            seen: new Map([[display, 1]]),
        });
    };
    for (const h of hymns) {
        addPerson(h.author, 'author', h);
        addPerson(h.translator, 'translator', h);
        addPerson(h.composer, 'composer', h);
        addPerson(h.arranger, 'arranger', h);
        addPerson(h.harmonizer, 'harmonizer', h);
    }
    // --- meter pages ----------------------------------------------------------
    const meterAcc = new Map();
    for (const h of hymns) {
        if (!h.meter || !h.meterSlug)
            continue;
        const existing = meterAcc.get(h.meterSlug);
        if (existing) {
            existing.hymns.push(refOf(h));
            continue;
        }
        meterAcc.set(h.meterSlug, { slug: h.meterSlug, meter: h.meter, hymns: [refOf(h)] });
    }
    // --- section pages --------------------------------------------------------
    // Book order, NOT hymn count: the sections are a printed sequence and reading
    // them out of order would misrepresent the book. A section whose range holds no
    // hymn in this corpus gets no page — the "never emit a dead end" rule.
    const sections = (source.sections ?? [])
        .map((s) => ({
        slug: s.slug,
        label: s.label,
        ...(s.group ? { group: s.group } : {}),
        ...(s.season ? { season: s.season } : {}),
        from: s.from,
        to: s.to,
        hymns: hymns.filter((h) => h.sectionSlug === s.slug).map(refOf),
    }))
        .filter((s) => s.hymns.length > 0)
        .sort((a, b) => a.from - b.from);
    // Resolve each person's canonical credit and freeze the variant list.
    const people = [...personAcc.values()].map(({ seen, ...p }) => ({
        ...p,
        name: canonicalCredit(seen),
        variants: [...seen.keys()].sort(),
    }));
    const byHymnCountThenName = (a, b) => b.hymns.length - a.hymns.length || a.slug.localeCompare(b.slug);
    return {
        source,
        hymns,
        tunes: [...tuneAcc.values()].filter((t) => t.hymns.length >= thresholds.tuneMinHymns).sort(byHymnCountThenName),
        people: people.filter((p) => p.hymns.length >= thresholds.personMinHymns).sort(byHymnCountThenName),
        meters: [...meterAcc.values()].filter((m) => m.hymns.length >= thresholds.meterMinHymns).sort(byHymnCountThenName),
        sections,
    };
}
/**
 * Every concrete route for a hymnal, in prerender order.
 *
 * This is what feeds `seo.json` `extraRoutes` — `prerender-bespoke.mjs` skips param
 * routes by design and takes concrete instances from there.
 */
export function hymnalRoutes(pages) {
    const { source } = pages;
    return [
        hymnalHomeRoute(source),
        ...pages.hymns.map((h) => hymnRoute(source, h.slug)),
        ...pages.tunes.map((t) => tuneRoute(source, t.slug)),
        ...pages.people.map((p) => personRoute(source, p.slug)),
        ...pages.meters.map((m) => meterRoute(source, m.slug)),
        ...pages.sections.map((s) => sectionRoute(source, s.slug)),
    ];
}
/**
 * Find slugs claimed by more than one distinct identity.
 *
 * MUST be run in a test over the real data, not assumed. Person slugs strip life
 * dates, so two different musicians credited as "J. S. Bach, 1685-1750" and
 * "J. S. Bach, 1802-1880" would collapse to one URL and silently merge two people's
 * work. A collision is a bug in the slug scheme, and the fix is to disambiguate —
 * never to let the last writer win.
 */
export function findSlugCollisions(pages) {
    const out = [];
    const check = (kind, rows) => {
        const bySlug = new Map();
        for (const r of rows) {
            const set = bySlug.get(r.slug) ?? new Set();
            set.add(r.identity);
            bySlug.set(r.slug, set);
        }
        for (const [slug, identities] of bySlug) {
            if (identities.size > 1)
                out.push({ kind, slug, identities: [...identities].sort() });
        }
    };
    check('hymn', pages.hymns.map((h) => ({ slug: h.slug, identity: h.number })));
    check('tune', pages.tunes.map((t) => ({ slug: t.slug, identity: t.name })));
    check('person', pages.people.map((p) => ({ slug: p.slug, identity: p.name })));
    check('meter', pages.meters.map((m) => ({ slug: m.slug, identity: m.meter })));
    // Each kind is checked WITHIN its own namespace, not across: routes are
    // `/tune/x` vs `/section/x`, so the same slug in two kinds is not a collision.
    check('section', pages.sections.map((s) => ({ slug: s.slug, identity: s.label })));
    return out;
}
/**
 * Merging person credits by slug base is right for typos and qualifiers, but it is
 * the one place this model can do real damage: two genuinely different musicians
 * who share a name would be merged into one page, silently attributing one's work to
 * the other.
 *
 * The signal is the credited BIRTH year. Typos shift it a little ("Bach, 1685-1759"
 * — the death year is wrong, birth agrees); two different people shift it a lot.
 * Anything over `toleranceYears` is reported for human review rather than merged
 * blindly or split blindly — the model cannot tell which without knowing the people.
 *
 * Callers should assert the returned set equals a REVIEWED known list, so a new
 * suspicious merge fails rather than slipping in.
 */
export function findSuspiciousPersonMerges(pages, toleranceYears = 15) {
    const out = [];
    for (const p of pages.people) {
        const years = p.variants
            .map((v) => /,\s*(?:b\.|d\.|c\.|fl\.)?\s*(\d{4})\s*-/.exec(v)?.[1])
            .filter((y) => Boolean(y))
            .map(Number);
        if (years.length < 2)
            continue;
        const spread = Math.max(...years) - Math.min(...years);
        if (spread > toleranceYears) {
            out.push({ slug: p.slug, variants: [...p.variants], birthYears: [...new Set(years)].sort(), spread });
        }
    }
    return out.sort((a, b) => b.spread - a.spread || a.slug.localeCompare(b.slug));
}
